import React, {
  useState,
  useRef,
  useEffect,
  useCallback
} from "react";

const TabbedView = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isFixed, setIsFixed] = useState(false);

  const navRef = useRef(null);
  const containerRef = useRef(null);
  const indicatorRef = useRef(null);
  const spacerRef = useRef(null);

  const navTopOffset = useRef(0);

  /* =========================
     INDICATOR POSITIONING
  ========================= */
  const updateIndicator = useCallback(() => {
    if (!containerRef.current || !indicatorRef.current) return;

    const buttons = containerRef.current.querySelectorAll(
      ".TabbedViewNavigationButton"
    );

    const activeButton = buttons[activeTab];
    if (!activeButton) return;

    indicatorRef.current.style.width = `${activeButton.offsetWidth}px`;
    indicatorRef.current.style.transform = `translateX(${activeButton.offsetLeft}px)`;
  }, [activeTab]);

  /* =========================
     INITIAL MEASUREMENTS
  ========================= */
  useEffect(() => {
    if (!navRef.current) return;

    const rect = navRef.current.getBoundingClientRect();
    navTopOffset.current = rect.top + window.scrollY;

    updateIndicator();
  }, [updateIndicator, tabs.length]);

  /* =========================
     SCROLL DETECTION
  ========================= */
  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const threshold =
        navTopOffset.current - parseInt(getComputedStyle(document.documentElement)
          .getPropertyValue("--topBarHeight"));

      if (scrollY >= threshold && !isFixed) {
        setIsFixed(true);
      } else if (scrollY < threshold && isFixed) {
        setIsFixed(false);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isFixed]);

  /* =========================
     RESIZE & ORIENTATION
  ========================= */
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (!navRef.current) return;
      navTopOffset.current =
        navRef.current.getBoundingClientRect().top + window.scrollY;
      updateIndicator();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", updateIndicator);
    window.addEventListener("orientationchange", updateIndicator);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateIndicator);
      window.removeEventListener("orientationchange", updateIndicator);
    };
  }, [updateIndicator]);

  return (
    <div className="TabbedView">
      {/* Spacer only when fixed */}
      {isFixed && <div ref={spacerRef} className="TabbedViewNavigationSpacer" />}

      {/* TAB BAR */}
      <div
        ref={navRef}
        className={`TabbedViewNavigation ${isFixed ? "is-fixed" : ""}`}
      >
        <div className="container" ref={containerRef}>
          <div className="TabIndicator" ref={indicatorRef} />

          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              className={`TabbedViewNavigationButton ${
                activeTab === index ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab(index);

                //..scroller focuser
                window.scrollTo({
                  top: document.querySelector(".TabbedViewContent").getBoundingClientRect().top
                      + window.scrollY
                      - 150,
                  behavior: "smooth"
                });

              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* CONTENT */}
      <div className="TabbedViewContent">
        {tabs[activeTab]?.content}
      </div>
    </div>
  );
};

export default TabbedView;
